import React, { useEffect, useState, useRef } from "react";
import { FiEye } from "react-icons/fi";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { formatView } from "../formatView";
import { useSelector } from "react-redux";
import ClientCalling from "../../api/ClientCalling";
import { IoMdClose } from "react-icons/io";

const StoryViewer = ({ stories, onClose, startIndex = 0 }) => {
    const controller = useSelector((state) => state?.controller?.controller);

    const [currentIndex, setCurrentIndex] = useState(startIndex);
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef(null);
    const navigate = useNavigate();
    const isClosingRef = useRef(false);
    const historyPushedRef = useRef(false);

    const storyTime = controller?.story_ads_time || 5;

    // Handle cleanup and close
    const handleClose = () => {
        if (isClosingRef.current) return;
        isClosingRef.current = true;

        // Clear interval
        clearInterval(intervalRef.current);

        // Exit fullscreen
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => { });
        }

        // Call onClose immediately
        onClose();
    };

    // Hide floating client FABs (scroll-to-top + download cluster) while viewer is open
    useEffect(() => {
        document.body.classList.add("story-open");
        return () => {
            document.body.classList.remove("story-open");
        };
    }, []);

    // Handle browser back button
    useEffect(() => {
        const handlePopState = (e) => {
            e.preventDefault();
            handleClose();
        };

        // Push a dummy state when story viewer opens
        if (!historyPushedRef.current) {
            window.history.pushState({ storyViewer: true }, '', window.location.href);
            historyPushedRef.current = true;
        }

        // Listen for back button
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);

            // Clean up history state on unmount only if we pushed it
            if (historyPushedRef.current && window.history.state?.storyViewer) {
                window.history.back();
            }
        };
    }, []);

    useEffect(() => {
        // Start progress automatically when component mounts
        startProgress();

        // Call viewPluse for the current story
        if (currentStory?._id) {
            viewPluse(currentStory._id);
        }

        // Optional: make browser full-screen
        if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => { });
        }

        return () => clearInterval(intervalRef.current);
    }, [currentIndex]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowRight") {
                handleNext();
            } else if (e.key === "ArrowLeft") {
                handlePrev();
            } else if (e.key === "Escape") {
                handleClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [currentIndex]);

    const startProgress = () => {
        setProgress(0);
        clearInterval(intervalRef.current);

        const step = 100 / (storyTime * 20); // 50ms per step
        intervalRef.current = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(intervalRef.current);
                    if (currentIndex < stories.data.length - 1) {
                        setCurrentIndex(prev => prev + 1);
                    } else {
                        handleClose();
                    }
                    return 100;
                }
                return prev + step;
            });
        }, 50);
    };

    const viewPluse = async (id) => {
        try {
            await ClientCalling.viewPluseReelsClient(id);
        } catch (error) {
            console.error('not view pluse');
        }
    };

    const handleNext = () => {
        clearInterval(intervalRef.current);
        if (currentIndex < stories.data.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            handleClose();
        }
    };

    const handlePrev = () => {
        clearInterval(intervalRef.current);
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    if (!stories || stories.data.length === 0) return null;

    const currentStory = stories.data[currentIndex];

    const handelClick = (id) => {
        handleClose();
        setTimeout(() => {
            navigate(`/list/${id}`);
        }, 100);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center z-50">
            {/* Image Container */}
            <div className="relative h-[100vh] w-auto md:w-[60vh] py-1 max-w-full">
                {/* Progress bar - Fixed overflow */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gray-600 w-full overflow-hidden z-30">
                    <div
                        className="h-full bg-client-primary rounded transition-all duration-100"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                </div>

                <img
                    src={currentStory.l_id.l_image}
                    alt={currentStory.l_id.l_name}
                    className="w-full h-[100vh] object-cover"
                />

                {/* Top Bar: View count (left) + Close button (right) */}
                <div className="absolute top-2 left-0 right-0 flex justify-between items-center px-2 z-20">
                    {/* View Count */}
                    <div className="bg-client-back text-client-font text-xs md:text-[12px] px-2 py-1 rounded-full flex items-center gap-2">
                        <FiEye /> {formatView(currentStory.l_view)}
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={handleClose}
                        className="text-client-font rounded-full h-8 w-8 font-bold transition flex items-center justify-center bg-client-back/20 hover:bg-client-back/40"
                    >
                        <IoMdClose />
                    </button>
                </div>

                {/* Bottom Info - Glass Effect */}
                <div
                    className="absolute bottom-4 left-1/2 w-[80%] -translate-x-1/2 flex items-center justify-between
                    bg-white/10 backdrop-blur-sm rounded-xl p-2 cursor-pointer z-30"
                    onClick={() => handelClick(currentStory.l_id?._id)}
                >
                    <div
                        className="gap-2 w-full mx-auto flex items-center justify-between
                        bg-white/10 backdrop-blur-sm rounded-lg p-2"
                    >
                        {/* Story Name */}
                        <p className="text-client-font text-sm md:text-base text-center truncate">
                            {currentStory?.l_id?.l_name}
                        </p>

                        {/* Get Prompt Button */}
                        <div
                            className="px-4 py-2 rounded-full bg-client-back text-client-font font-medium hover:opacity-90 transition cursor-pointer whitespace-nowrap"
                        >
                            Get Prompt
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Overlay (Instagram style) */}
            <div className="absolute inset-0 flex z-10">
                {/* Left side - Previous */}
                <div
                    className="w-1/2 h-full cursor-pointer"
                    onClick={handlePrev}
                ></div>

                {/* Right side - Next */}
                <div
                    className="w-1/2 h-full cursor-pointer"
                    onClick={handleNext}
                ></div>
            </div>
        </div>
    );
};

export default StoryViewer;